/**
 * Pick bases, grouped by game.
 *
 * Used twice with the same shape but different meaning: granting bases to a
 * company, and narrowing a user inside what its company already holds. Both are
 * long lists — around 142 bases across 77 games — so it searches, groups, and
 * lets a whole game be taken or dropped at once.
 *
 * Bases carrying a snapshot editor are labelled with their pipeline, because
 * granting one of those is what decides who can open that editor at all.
 *
 * The chrome comes from settingsPalette so this and SelectorCompanies, which
 * share a screen, look like the same control. Pass `fill` where it sits in a
 * pane that has already been given a height — it then takes what it is given
 * instead of stopping at a fixed maximum.
 */

import React, { useMemo, useState } from "react";
import Checkbox from "@material-ui/core/Checkbox";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import { SEARCH_INPUT_PROPS, useChecklistStyles } from "./settingsPalette";

export default function SelectorBases(props) {
  const classes = useChecklistStyles();
  const [query, setQuery] = useState("");

  const selected = useMemo(() => new Set((props.selectedBaseIDs || []).map(String)), [props.selectedBaseIDs]);
  const available = props.bases || [];

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matching = needle
      ? available.filter(
        (base) =>
          (base.title || "").toLowerCase().includes(needle) ||
            (base.gameTitle || "").toLowerCase().includes(needle)
      )
      : available;

    const byGame = new Map();
    for (const base of matching) {
      const key = base.gameTitle || "(unknown game)";
      if (!byGame.has(key)) byGame.set(key, []);
      byGame.get(key).push(base);
    }
    return Array.from(byGame.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [available, query]);

  const setSelection = (nextSet) => props.onChange(Array.from(nextSet));

  const toggleBase = (baseID) => {
    const next = new Set(selected);
    if (next.has(String(baseID))) {
      next.delete(String(baseID));
    } else {
      next.add(String(baseID));
    }
    setSelection(next);
  };

  const toggleGame = (bases, shouldSelect) => {
    const next = new Set(selected);
    bases.forEach((base) => {
      if (shouldSelect) {
        next.add(String(base._id));
      } else {
        next.delete(String(base._id));
      }
    });
    setSelection(next);
  };

  return (
    <div className={`${classes.wrapper} ${props.fill ? classes.wrapperFill : ""}`}>
      <div className={classes.toolbar}>
        <TextField
          className={classes.search}
          size="small"
          placeholder="Search bases or games"
          InputProps={SEARCH_INPUT_PROPS}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        {/* Counts what is listed: a selection can hold ids this caller is not
            shown (a narrowed base granter), and those are not its to count. */}
        <span className={classes.count}>
          {available.filter((base) => selected.has(String(base._id))).length} / {available.length}
        </span>
        <Button className={classes.linkButton} onClick={() => setSelection(new Set(available.map((b) => String(b._id))))}>
          All
        </Button>
        <Button className={classes.linkButton} onClick={() => setSelection(new Set())}>
          None
        </Button>
      </div>

      <div className={`${classes.scroller} ${props.fill ? classes.scrollerFill : classes.scrollerCapped}`}>
        {groups.length === 0 && <div className={classes.empty}>{props.emptyText || "No bases to show."}</div>}
        {groups.map(([gameTitle, bases]) => {
          const allSelected = bases.every((base) => selected.has(String(base._id)));
          return (
            <div key={gameTitle}>
              <div className={classes.groupHeader}>
                <Typography className={classes.groupTitle}>{gameTitle}</Typography>
                <Button className={classes.linkButton} onClick={() => toggleGame(bases, !allSelected)}>
                  {allSelected ? "Clear" : "Select"}
                </Button>
              </div>
              {bases.map((base) => (
                <div
                  key={String(base._id)}
                  className={classes.row}
                  onClick={() => toggleBase(base._id)}
                >
                  <Checkbox size="small" checked={selected.has(String(base._id))} onChange={() => toggleBase(base._id)} />
                  <span className={classes.rowLabel}>{base.title}</span>
                  {base.snapshotEditorFlow && <span className={classes.tag}>{base.snapshotEditorFlow}</span>}
                  {/* Created in the dashboard rather than seeded from git. */}
                  {base.managedBy && <span className={classes.tag}>runtime</span>}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
